import type * as THREE from 'three'
import type { MateEntityRef } from '../../assembly'
import type { GlbHierarchyNode } from '../../imported-model/loaders/glb'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { collectImportedModelTopologyRepositories } from '../../imported-model/topology/ImportedModelTopologyRepository'
import {
  areImportedBodyBindingIdsEquivalent,
  resolveImportedBodyOccurrenceNodeId,
} from '../../imported-model/utils'
import {
  LinearComponentPatternReferenceDescriptorKind,
  type LinearComponentPatternReferenceDescriptor,
  type LinearComponentPatternState,
} from '../../pattern'
import {
  areProjectModelRuntimeNodeIdsEquivalent,
  isProjectModelRuntimeNodeIdInside,
  normalizeProjectModelRuntimeNodeId,
} from './project-model-node-identity'

/** 线性阵列定义中保存普通场景引用的字段。 */
const LINEAR_COMPONENT_PATTERN_REFERENCE_OBJECT_ID_KEYS = [
  'direction1ReferenceObjectId',
  'direction1RotationAxisObjectId',
  'direction1ReferenceGeometryObjectId',
  'direction1SelectedReferenceObjectId',
  'direction2ReferenceObjectId',
  'direction2ReferenceGeometryObjectId',
  'direction2SelectedReferenceObjectId',
] as const satisfies readonly (keyof LinearComponentPatternState)[]

/** 线性阵列定义中保存稳定引用描述符的字段。 */
const LINEAR_COMPONENT_PATTERN_REFERENCE_DESCRIPTOR_KEYS = [
  'direction1ReferenceDescriptor',
  'direction1RotationAxisDescriptor',
  'direction1ReferenceGeometryDescriptor',
  'direction1SelectedReferenceDescriptor',
  'direction2ReferenceDescriptor',
  'direction2ReferenceGeometryDescriptor',
  'direction2SelectedReferenceDescriptor',
] as const satisfies readonly (keyof LinearComponentPatternState)[]

/**
 * 项目模型局部子树语义图闭环验证器。
 *
 * hierarchy 是 occurrence 集合的权威来源；该模块保证 Object3D、selection、sceneBindings、
 * topology、Mate 与线性阵列只引用当前最终子树中存在的节点和 body，不修改任何业务对象。
 */
export class ProjectModelSubtreeGraphValidator {
  /**
   * 在 detached 子树进入场景前校验全部语义投影已经闭合。
   *
   * @param input 最终子树、来源身份映射及已重绑关系文档。
   */
  public assertClosed(input: PuppyCAD.ProjectModelSubtreeSemanticGraphValidationInput): void {
    this.assertFinalRootIdentity(input.finalizedHierarchy, input.targetAssemblyNodeId)

    const knownNodeIds = new Set<string>()
    const knownBodyBindingIds = new Set<string>()
    const staleSourceNodeIds = new Set<string>()
    const collectHierarchy = (node: GlbHierarchyNode, target: Set<string>): void => {
      if (target === knownNodeIds) {
        this.assertHierarchyRuntimeObjectReferences(
          node,
          input.importedResult.sceneBindings.byObject3DUuid,
        )
      }
      const assemblyNodeId = this.resolveDeclaredAssemblyNodeId(node)
      if (assemblyNodeId) {
        target.add(assemblyNodeId)
      }
      node.meta?.bodyEntries?.forEach((entry) => {
        if (target === knownNodeIds && entry.bodyBindingId.trim()) {
          knownBodyBindingIds.add(entry.bodyBindingId.trim())
        }
      })
      node.children.forEach((child) => collectHierarchy(child, target))
    }
    collectHierarchy(input.finalizedHierarchy, knownNodeIds)
    collectHierarchy(input.generatedHierarchy, staleSourceNodeIds)
    input.explicitNodeAliases.forEach((targetNodeId, sourceNodeId) => {
      if (!areProjectModelRuntimeNodeIdsEquivalent(sourceNodeId, targetNodeId)) {
        staleSourceNodeIds.add(normalizeProjectModelRuntimeNodeId(sourceNodeId))
      }
    })
    input.bodyOccurrenceRebindings.forEach((rebinding) => {
      const sourceNodeId = resolveImportedBodyOccurrenceNodeId(rebinding.sourceBodyBindingId)
      if (!areProjectModelRuntimeNodeIdsEquivalent(sourceNodeId, rebinding.targetAssemblyNodeId)) {
        staleSourceNodeIds.add(normalizeProjectModelRuntimeNodeId(sourceNodeId))
      }
    })
    staleSourceNodeIds.delete('')

    input.importedResult.root.traverse((object) => {
      ImportedModelObjectMetadataService.collectBodyBindingIds(object)
        .forEach(bodyBindingId => knownBodyBindingIds.add(bodyBindingId))
    })
    collectImportedModelTopologyRepositories(input.importedResult.root).forEach((repository) => {
      repository.listBodyBindingIds().forEach((bodyBindingId) => {
        if (bodyBindingId.trim()) {
          knownBodyBindingIds.add(bodyBindingId.trim())
        }
      })
    })

    const targetAssemblyNodeId = normalizeProjectModelRuntimeNodeId(input.targetAssemblyNodeId)
    const isScopedNodeId = (nodeId: string): boolean => {
      const normalizedNodeId = normalizeProjectModelRuntimeNodeId(nodeId)
      if (!normalizedNodeId) {
        return false
      }
      return isProjectModelRuntimeNodeIdInside(normalizedNodeId, targetAssemblyNodeId)
        || [...staleSourceNodeIds].some(sourceNodeId => (
          isProjectModelRuntimeNodeIdInside(normalizedNodeId, sourceNodeId)
        ))
    }
    const isKnownNodeId = (nodeId: string): boolean => (
      [...knownNodeIds].some(knownNodeId => (
        areProjectModelRuntimeNodeIdsEquivalent(knownNodeId, nodeId)
      ))
    )
    const assertKnownNodeId = (
      nodeId: string,
      context: string,
      requireKnownNode: boolean,
    ): void => {
      if ((requireKnownNode || isScopedNodeId(nodeId)) && !isKnownNodeId(nodeId)) {
        throw new Error(
          `项目模型局部替换 ${context} 引用未知装配节点：assemblyNodeId=${nodeId}`,
        )
      }
    }
    const isKnownBodyBindingId = (bodyBindingId: string): boolean => (
      [...knownBodyBindingIds].some(knownBodyBindingId => (
        knownBodyBindingId === bodyBindingId
        || areImportedBodyBindingIdsEquivalent(knownBodyBindingId, bodyBindingId)
      ))
    )
    const assertTopologyRef = (
      topologyRef: PuppyCAD.AssemblyTopologySelectionRef | null | undefined,
      context: string,
      requireKnownNode: boolean,
    ): void => {
      if (!topologyRef) {
        return
      }
      assertKnownNodeId(topologyRef.assemblyNodeId, context, requireKnownNode)
      if ((requireKnownNode || isScopedNodeId(topologyRef.assemblyNodeId))
        && !isKnownBodyBindingId(topologyRef.bodyBindingId)) {
        throw new Error(
          `项目模型局部替换 ${context} 引用未知 body：bodyBindingId=${topologyRef.bodyBindingId}`,
        )
      }
    }
    const assertMateEntity = (
      entity: MateEntityRef,
      context: string,
      requireKnownNode: boolean,
    ): void => {
      assertKnownNodeId(entity.assemblyNodeId, context, requireKnownNode)
      assertKnownNodeId(entity.componentInstanceId, context, requireKnownNode)
      assertTopologyRef(entity.topologyRef, context, requireKnownNode)
      entity.referenceDependency?.driverComponentInstanceIds.forEach((assemblyNodeId) => {
        assertKnownNodeId(assemblyNodeId, context, requireKnownNode)
      })
      entity.referenceDependency?.driverTopologyRefs.forEach((topologyRef) => {
        assertTopologyRef(topologyRef, context, requireKnownNode)
      })
    }

    input.importedResult.root.traverse((object) => {
      const metadata = ImportedModelObjectMetadataService.readOwnMetadata(object)
      if (!metadata) {
        return
      }
      if (metadata.assemblyNodeId) {
        assertKnownNodeId(metadata.assemblyNodeId, 'Object3D metadata', false)
      }
      if (metadata.selectionTarget?.assemblyNodeId) {
        assertKnownNodeId(metadata.selectionTarget.assemblyNodeId, 'Object3D selection target', false)
      }
      Object.entries(metadata.bodyBindingSelectionTargetsById ?? {}).forEach(([
        bodyBindingId,
        selectionTarget,
      ]) => {
        assertKnownNodeId(selectionTarget.assemblyNodeId, 'Object3D body selection target', false)
        if (isScopedNodeId(selectionTarget.assemblyNodeId)
          && !isKnownBodyBindingId(bodyBindingId)) {
          throw new Error(
            `项目模型局部替换 Object3D body selection target 引用未知 body：bodyBindingId=${bodyBindingId}`,
          )
        }
      })
    })
    Object.entries(input.importedResult.selectionIndex.byObject3DUuid).forEach(([
      objectId,
      selectionTarget,
    ]) => {
      if (!input.importedResult.sceneBindings.byObject3DUuid[objectId]) {
        throw new Error(`项目模型局部替换 selection index 引用未知 Object3D：objectId=${objectId}`)
      }
      if (selectionTarget.assemblyNodeId) {
        assertKnownNodeId(selectionTarget.assemblyNodeId, 'selection index', false)
      }
    })
    Object.entries(input.importedResult.sceneBindings.byAssemblyNodeId).forEach(([
      assemblyNodeId,
      objectIds,
    ]) => {
      assertKnownNodeId(assemblyNodeId, 'sceneBindings', false)
      objectIds.forEach((objectId) => {
        const object = input.importedResult.sceneBindings.byObject3DUuid[objectId]
        if (!object) {
          throw new Error(`项目模型局部替换 sceneBindings 引用未知 Object3D：objectId=${objectId}`)
        }
        const metadataAssemblyNodeId = ImportedModelObjectMetadataService
          .readOwnMetadata(object)?.assemblyNodeId
        if (metadataAssemblyNodeId
          && !areProjectModelRuntimeNodeIdsEquivalent(metadataAssemblyNodeId, assemblyNodeId)) {
          throw new Error(
            `项目模型局部替换 sceneBindings 与 Object3D metadata 身份不一致：objectId=${objectId}`,
          )
        }
      })
    })
    Object.values(input.importedResult.sceneBindings.byProjectNodeKey ?? {})
      .flat()
      .forEach((objectId) => {
        if (!input.importedResult.sceneBindings.byObject3DUuid[objectId]) {
          throw new Error(
            `项目模型局部替换 project sceneBindings 引用未知 Object3D：objectId=${objectId}`,
          )
        }
      })
    collectImportedModelTopologyRepositories(input.importedResult.root).forEach((repository) => {
      repository.listBodyBindingIds().forEach((bodyBindingId) => {
        const assemblyNodeId = resolveImportedBodyOccurrenceNodeId(bodyBindingId)
        if (assemblyNodeId) {
          assertKnownNodeId(assemblyNodeId, 'topology occurrence', false)
        }
      })
    })
    input.mateDocuments.forEach((mateDocument) => {
      const document = mateDocument.document
      const requireKnownNode = !mateDocument.restoreAsRootDocument
      const context = requireKnownNode ? 'scoped Mate' : 'Mate'
      assertKnownNodeId(document.assemblyId, context, requireKnownNode)
      Object.entries(document.components).forEach(([componentKey, component]) => {
        assertKnownNodeId(componentKey, context, requireKnownNode)
        assertKnownNodeId(component.componentInstanceId, context, requireKnownNode)
      })
      document.mates.forEach((mate) => {
        assertKnownNodeId(mate.assemblyId, context, requireKnownNode)
        assertMateEntity(mate.firstEntity, context, requireKnownNode)
        assertMateEntity(mate.secondEntity, context, requireKnownNode)
        if (mate.referenceEntity) {
          assertMateEntity(mate.referenceEntity, context, requireKnownNode)
        }
      })
    })
    input.linearComponentPatternDocuments.forEach((patternDocument) => {
      assertKnownNodeId(patternDocument.ownerAssemblyNodeId, '线性阵列 owner', true)
      patternDocument.document.records.forEach((record) => {
        const context = `线性阵列 owner=${patternDocument.ownerAssemblyNodeId}, featureId=${record.featureId}`
        const state = record.definition.state
        const assertRuntimeReference = (objectId: string | null | undefined): void => {
          const normalizedObjectId = objectId?.trim() ?? ''
          if (!normalizedObjectId) {
            return
          }
          if (input.importedResult.sceneBindings.byObject3DUuid[normalizedObjectId]) {
            return
          }
          if (isKnownNodeId(normalizedObjectId) || isKnownBodyBindingId(normalizedObjectId)) {
            return
          }
          throw new Error(
            `项目模型局部替换 ${context} 引用未知运行对象：objectId=${normalizedObjectId}`,
          )
        }
        state.componentObjectIds.forEach(assertRuntimeReference)
        LINEAR_COMPONENT_PATTERN_REFERENCE_OBJECT_ID_KEYS.forEach((key) => {
          assertRuntimeReference(state[key] as string | null | undefined)
        })
        LINEAR_COMPONENT_PATTERN_REFERENCE_DESCRIPTOR_KEYS.forEach((key) => {
          const descriptor = state[key] as LinearComponentPatternReferenceDescriptor | null | undefined
          if (!descriptor) {
            return
          }
          if (descriptor.kind === LinearComponentPatternReferenceDescriptorKind.SceneObject) {
            assertRuntimeReference(descriptor.objectId)
            return
          }
          assertRuntimeReference(descriptor.hostObjectId)
          assertTopologyRef(descriptor.assemblyTopologyRef, context, true)
        })
      })
    })
  }

  /** 校验 hierarchy 节点使用的运行时场景对象引用。 */
  private assertHierarchyRuntimeObjectReferences(
    node: GlbHierarchyNode,
    objectByUuid: Readonly<Record<string, THREE.Object3D>>,
  ): void {
    const object = objectByUuid[node.objectId]
    if (object) {
      const hierarchyAssemblyNodeId = this.resolveDeclaredAssemblyNodeId(node)
      const objectAssemblyNodeId = normalizeProjectModelRuntimeNodeId(
        ImportedModelObjectMetadataService.readOwnMetadata(object)?.assemblyNodeId,
      )
      if (hierarchyAssemblyNodeId && !objectAssemblyNodeId) {
        throw new Error(
          `项目模型局部替换 hierarchy 对象缺少运行时装配身份：objectId=${node.objectId}`,
        )
      }
      if (hierarchyAssemblyNodeId
        && !areProjectModelRuntimeNodeIdsEquivalent(hierarchyAssemblyNodeId, objectAssemblyNodeId)) {
        throw new Error(
          '项目模型局部替换 hierarchy 与 Object3D metadata 身份不一致：'
          + `objectId=${node.objectId}, hierarchy=${hierarchyAssemblyNodeId}, object=${objectAssemblyNodeId}`,
        )
      }
      return
    }
    if (node.nodeType !== 'Body') {
      throw new Error(`项目模型局部替换 hierarchy 引用未知 Object3D：objectId=${node.objectId}`)
    }

    const selectionObjectId = node.meta?.selectionObjectId?.trim() ?? ''
    if (!selectionObjectId || !objectByUuid[selectionObjectId]) {
      throw new Error(
        `项目模型局部替换虚拟 Body 引用未知选择对象：bodyObjectId=${node.objectId}, selectionObjectId=${selectionObjectId}`,
      )
    }
    node.meta?.highlightTargets?.forEach((target) => {
      if (!objectByUuid[target.objectId]) {
        throw new Error(
          `项目模型局部替换虚拟 Body 引用未知高亮对象：bodyObjectId=${node.objectId}, highlightObjectId=${target.objectId}`,
        )
      }
    })
  }

  /** 解析 hierarchy 显式声明的唯一 occurrence 身份。 */
  private resolveDeclaredAssemblyNodeId(node: GlbHierarchyNode): string {
    const metadataAssemblyNodeId = normalizeProjectModelRuntimeNodeId(node.meta?.assemblyNodeId)
    if (metadataAssemblyNodeId) {
      return metadataAssemblyNodeId
    }
    const bodyAssemblyNodeIds = new Set((node.meta?.bodyEntries ?? [])
      .map(entry => normalizeProjectModelRuntimeNodeId(
        resolveImportedBodyOccurrenceNodeId(entry.bodyBindingId),
      ))
      .filter(Boolean))
    return bodyAssemblyNodeIds.size === 1 ? [...bodyAssemblyNodeIds][0]! : ''
  }

  /** 校验最终 hierarchy 根与结构事务目标一致。 */
  private assertFinalRootIdentity(
    hierarchy: GlbHierarchyNode,
    targetAssemblyNodeId: string,
  ): void {
    const actualNodeId = normalizeProjectModelRuntimeNodeId(hierarchy.meta?.assemblyNodeId)
    const expectedNodeId = normalizeProjectModelRuntimeNodeId(targetAssemblyNodeId)
    if (!areProjectModelRuntimeNodeIdsEquivalent(actualNodeId, expectedNodeId)) {
      throw new Error(
        `项目模型局部替换最终根身份不一致：expected=${expectedNodeId}, actual=${actualNodeId}`,
      )
    }
  }
}
